import {Component, OnInit} from '@angular/core';
import {MatDialogRef} from '@angular/material/dialog';
import {Observable} from 'rxjs';
import {map, takeUntil} from 'rxjs/operators';
import {UnitShort} from '@common-module/modelinterfaces/unit-short.model';
import {User} from '@common-module/modelinterfaces/user.model';

import {SelectedService} from '../../../shared/services/selected.service';
import {UnitGroupsService} from '../../../shared/services/unit-groups.service';
import {PublicAccess} from '@common-module/modelinterfaces/public-access.model';
import {PublicAccessService} from '@common-module/services/public-access.service';
import {UiSpinnerService} from '@common-module/app-services/ui-spinner.service';
import {AppMessageService} from '@common-module/app-services/app-message.service';
import {AuthUserService} from '@common-module/app-services/auth-user.service';
import {LocalDate, Instant, ZoneId} from '@js-joda/core';
import {TimeZoneUtil} from '@common-module/utils/time-zone-util';
import {DateTimePickerValue} from '@common-module/app-components/date-controls/date-time-picker/date-time-picker.models';
import {AppDateTimePickerComponent} from '@common-module/app-components/date-controls/date-time-picker/date-time-picker.component';
import {PublicLinkPeriod} from './public-link-period';
import {BaseUnsubscribeComponent} from "@common-module/app-components/base-unsubscribe.component";
import { DialogComponent } from '@common-module/dialog/dialog/dialog.component';
import { DialogContentDirective } from '@common-module/dialog/dialog/dialog-content.directive';
import { MatTooltip } from '@angular/material/tooltip';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { DialogActionsDirective } from '@common-module/dialog/dialog/dialog-actions.directive';
import { ButtonCancelComponent } from '@common-module/buttons/base/concrete/button-cancel/button-cancel.component';
import { ButtonPrimaryComponent } from '@common-module/buttons/base/style/button-primary/button-primary.component';
import { ButtonCloseComponent } from '@common-module/buttons/base/concrete/button-close/button-close.component';
import { AsyncPipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ZonedDateTimeFormatterPipe } from '@common-module/pipes/zoned-date-time-formatter.pipe';


@Component({
    selector: 'app-public-link-dialog',
    host: {
      '[class.link-generated]': '!!link'
    },
    templateUrl: './public-link-dialog.component.html',
    styleUrls: ['./public-link-dialog.component.scss', '../../../../../../common-module/src/lib/app-styles/dialog-common.scss'],
    imports: [DialogComponent,
      DialogContentDirective,
      MatTooltip,
      AppDateTimePickerComponent,
      MatFormField,
      MatLabel,
      MatInput,
      DialogActionsDirective,
      ButtonCancelComponent,
      ButtonPrimaryComponent,
      ButtonCloseComponent,
      AsyncPipe,
      TranslateModule,
      ZonedDateTimeFormatterPipe]
})

export class PublicLinkDialogComponent extends BaseUnsubscribeComponent implements OnInit {

  public authUser$: Observable<User>;
  public readonly period = new PublicLinkPeriod(LocalDate.now());
  public periodValid = true;
  public link = '';
  public generatedExpiration: string | null = null;

  public expirationInZone(zone: string) {
    return this.generatedExpiration
      ? Instant.parse(this.generatedExpiration).atZone(zone ? ZoneId.of(zone) : TimeZoneUtil.BROWSER_ZONE_ID)
      : null;
  }
  public selectedUnitsCount = 0;
  public selectedUnitNames = '';

  constructor(private appMessageService: AppMessageService,
              private authUserService: AuthUserService,
              private dialogRef: MatDialogRef<PublicLinkDialogComponent>,
              private publicAccessService: PublicAccessService,
              private selectedService: SelectedService,
              private uiSpinnerService: UiSpinnerService,
              private unitGroupsService: UnitGroupsService) {
    super();
  }

  ngOnInit(): void {
    this.authUser$ = this.authUserService.currentUser$;
    this.selectedService.selectedUnitIdsSet$.pipe(
      map(idSet => this.getUnitListByIdSet(idSet)),
      takeUntil(this.destroy)
    ).subscribe(units => {
      this.selectedUnitsCount = units.length;
      this.selectedUnitNames = units.map(u => u.name).join('\n');
    })
  }

  private getUnitListByIdSet(idSet: Set<number>): UnitShort[] {
    const units = [];
    idSet.forEach(id => {
      const unit = this.unitGroupsService.getInstantWithoutGroups().find(el => el.id === id);
      if (unit) {
        units.push(unit);
      }
    });
    return units;
  }

  public changeDateTimeRange(timeRange: DateTimePickerValue): void {
    if (!timeRange) {
      return;
    }
    this.period.select(timeRange);
    this.link = '';
  }

  public onClose(): void {
    this.dialogRef.close();
  }

  public onGenerateLink(): void {
    if (!this.periodValid) {
      return;
    }
    const expiration = this.period.expirationIso(TimeZoneUtil.BROWSER_ZONE_ID);
    const body = PublicAccess.toCreate(
      expiration,
      this.getUnitListByIdSet(this.selectedService.getInstantUnitIdsSet())
    );
    this.uiSpinnerService.show();
    this.publicAccessService.create(body).subscribe(
      settings => {
        this.generatedExpiration = expiration;
        this.link = settings.url;
        this.uiSpinnerService.stop();
      }
    );
  }

  public copyToClipboard(): void {
    this.appMessageService.copyToClipBoard(this.link);
  }
}
